/* Projects: the client's projects the work is for, and who runs each.
 *
 * A job must be for a project, and there was no way to make one here (owner, 2026-10-08:
 * "No way to create project in the UI"). The list answers what a manager opens it for — which
 * projects are mine, how far along, what is late — and each project opens on its jobs. The
 * same form makes a project from this screen and from the middle of making a job, so nobody
 * has to leave a half-filled job to go and make its project.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { FormEvent, ReactNode } from 'react'
import {
  EmptyByFilter, EmptyFirstTime, Failed, Loading, NoAccess, ScreenState, TooMuchData, useToast,
} from '@jackdesk/shell'
import type { ErpError } from '../api'
import { useErp } from '../context'
import { formatCurrency, formatDate } from '../format'
import type { ProjectBody, ProjectEstimate, WorkProjectDetail, WorkProjectRow, WorkProjects, WorkRow } from './types'
import { PeoplePicker, PlainField, usePersonName } from './Pickers'
import { PeopleChange } from './PeopleChange'
import { usePacksActive } from './usePacks'
import { findKind, homeLabel, stateLabel, stateTone } from './words'

/** My jobs | Projects, at the top of both. */
export function WorkTabs({ on, onJobs, onProjects, jobsLabel }: {
  on: 'jobs' | 'projects'; onJobs: () => void; onProjects?: () => void; jobsLabel: string
}) {
  if (!onProjects) return null
  return (
    <nav className="row" aria-label="Work" style={{ gap: 0, borderBottom: '1px solid var(--line)' }}>
      {([['jobs', jobsLabel, onJobs], ['projects', 'Projects', onProjects]] as const).map(([key, text, go]) => (
        <button key={key} className="btn btn-quiet" aria-current={on === key ? 'page' : undefined}
                onClick={go}
                style={{ borderRadius: 0, borderBottom: on === key ? '2px solid var(--accent)' : '2px solid transparent',
                         fontWeight: on === key ? 600 : 400 }}>
          {text}
        </button>
      ))}
    </nav>
  )
}

const PREFS = 'erp.projects.prefs'

function readPrefs(): { scope: 'all' | 'managed'; closed: boolean } {
  try {
    const raw = localStorage.getItem(PREFS)
    if (raw) return { scope: 'all', closed: false, ...(JSON.parse(raw) as object) }
  } catch { /* a private window starts with the defaults */ }
  return { scope: 'all', closed: false }
}

export function WorkProjects({ onOpen, onJobs }: {
  onOpen: (name: string) => void
  onJobs: () => void
}) {
  const erp = useErp()
  const { active } = usePacksActive()
  // People by the names the work screens use everywhere; the org chart adds their designation.
  const nameOf = usePersonName()
  const [prefs, setPrefs] = useState(readPrefs)
  const [data, setData] = useState<WorkProjects | null>(null)
  const [failed, setFailed] = useState<ErpError | null>(null)
  const [making, setMaking] = useState(false)
  const [q, setQ] = useState('')

  const load = useCallback((signal?: AbortSignal) => {
    setFailed(null)
    erp.workProjects(prefs, signal).then(setData).catch((e) => {
      if ((e as Error).name !== 'AbortError') setFailed(e as ErpError)
    })
  }, [erp, prefs])
  useEffect(() => {
    const controller = new AbortController()
    load(controller.signal)
    return () => controller.abort()
  }, [load])

  const update = (next: Partial<typeof prefs>) => {
    const merged = { ...prefs, ...next }
    setPrefs(merged)
    try { localStorage.setItem(PREFS, JSON.stringify(merged)) } catch { /* nothing depends on it */ }
  }

  const shown = useMemo(() => {
    const s = q.trim().toLowerCase()
    return (data?.projects ?? []).filter((p) => !s || [p.project_name, p.customer, p.name,
      p.manager ? nameOf(p.manager) : '']
      .some((v) => String(v ?? '').toLowerCase().includes(s)))
  }, [data, q, nameOf])

  return (
    <div className="page" style={{ display: 'grid', gap: 14 }}>
      <WorkTabs on="projects" onJobs={onJobs} onProjects={() => undefined} jobsLabel={homeLabel(active)} />
      <div className="row row-between" style={{ flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h1>Projects</h1>
          <p className="muted small" style={{ marginTop: 4 }}>
            Each client project, who runs it, and how its work stands.
          </p>
        </div>
        {data?.may_create && !making ? (
          <button className="btn btn-primary" onClick={() => setMaking(true)}>New project</button>
        ) : null}
      </div>

      {making ? (
        <section className="card card-pad" aria-label="New project">
          <ProjectForm managerRoles={data?.manager_roles ?? []}
            onDone={(name) => { setMaking(false); onOpen(name) }}
            onCancel={() => setMaking(false)} />
        </section>
      ) : null}

      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <div className="row" role="group" aria-label="Whose"
             style={{ gap: 0, border: '1px solid var(--line)', borderRadius: 'var(--r-sm)', overflow: 'hidden' }}>
          {([['all', 'All'], ['managed', 'I run']] as const).map(([v, text]) => (
            <button key={v} className={`btn btn-sm ${prefs.scope === v ? 'btn-primary' : 'btn-quiet'}`}
                    aria-pressed={prefs.scope === v} style={{ borderRadius: 0 }}
                    onClick={() => update({ scope: v })}>{text}</button>
          ))}
        </div>
        <label className="check small">
          <input type="checkbox" checked={prefs.closed} onChange={(e) => update({ closed: e.target.checked })} />
          Show finished
        </label>
        <span className="grow" />
        <input className="input" type="search" placeholder="Find a project, client or manager"
               aria-label="Find a project" value={q} onChange={(e) => setQ(e.target.value)}
               style={{ maxWidth: 280 }} />
      </div>

      {failed ? <Failed message={failed.message} requestId={failed.requestId} onRetry={() => load()} /> : null}
      {!failed && !data ? <Loading rows={6} label="Reading the projects" /> : null}
      {data && !data.projects.length ? (
        <EmptyFirstTime what={prefs.scope === 'managed' ? 'projects you run' : 'projects'}
          action={data.may_create && !making
            ? <button className="btn btn-primary" onClick={() => setMaking(true)}>New project</button>
            : undefined} />
      ) : null}
      {data && data.projects.length && !shown.length ? <EmptyByFilter onClear={() => setQ('')} /> : null}
      {data?.truncated ? <TooMuchData count={data.projects.length} /> : null}

      {shown.length ? (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table className="tbl">
            <thead>
              <tr><th>Project</th><th>Project manager</th><th>Dates</th><th>Progress</th><th>Work</th></tr>
            </thead>
            <tbody>
              {shown.map((p) => (
                <tr key={p.name} tabIndex={0} onClick={() => onOpen(p.name)}
                    onKeyDown={(e) => { if (e.key === 'Enter') onOpen(p.name) }}>
                  <td>
                    <b>{p.project_name}</b>
                    {p.status !== 'Open' ? <span className="chip" style={{ marginLeft: 6 }}>{p.status}</span> : null}
                    <div className="small muted">{p.customer ?? 'No client'} · {p.name}</div>
                  </td>
                  <td className="small">
                    {p.manager ? nameOf(p.manager) : <span className="muted">Nobody yet</span>}
                    {p.manager_card?.designation ? <div className="muted">{p.manager_card.designation}</div> : null}
                  </td>
                  <td className="small">{dates(p)}</td>
                  <td style={{ minWidth: 110 }}><Progress value={p.percent_complete} /></td>
                  <td className="small">
                    {p.counts ? `${p.counts.open_jobs} open of ${p.counts.jobs}` : ''}
                    {p.counts?.late ? <span className="chip chip-danger" style={{ marginLeft: 6 }}>{p.counts.late} late</span> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  )
}

function dates(p: Pick<WorkProjectRow, 'expected_start_date' | 'expected_end_date'>): string {
  const from = p.expected_start_date ? formatDate(p.expected_start_date) : ''
  const to = p.expected_end_date ? formatDate(p.expected_end_date) : ''
  return from && to ? `${from} – ${to}` : to ? `Due ${to}` : from ? `From ${from}` : '—'
}

function Progress({ value }: { value?: number | null }) {
  const v = Math.max(0, Math.min(100, Math.round(Number(value ?? 0))))
  return (
    <div title={`${v}% done`} aria-label={`${v}% done`}
         style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 6, alignItems: 'center' }}>
      <div style={{ height: 6, borderRadius: 3, background: 'var(--line)', overflow: 'hidden' }}>
        <div style={{ width: `${v}%`, height: '100%', background: 'var(--accent)' }} />
      </div>
      <span className="small muted">{v}%</span>
    </div>
  )
}

/* ── one project ─────────────────────────────────────────────────────── */

export function WorkProject({ name, onOpen, onBack, onMake }: {
  name: string
  onOpen: (task: string) => void
  onBack: () => void
  /** Make a piece of work already on this project. */
  onMake: (pack: string, kind: string, project: string) => void
}) {
  const erp = useErp()
  const toast = useToast()
  const { active } = usePacksActive()
  const nameOf = usePersonName()
  const [detail, setDetail] = useState<WorkProjectDetail | null>(null)
  const [failed, setFailed] = useState<ErpError | null>(null)
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(false)

  const load = useCallback((signal?: AbortSignal) => {
    setFailed(null)
    erp.workProject(name, signal).then(setDetail).catch((e) => {
      if ((e as Error).name !== 'AbortError') setFailed(e as ErpError)
    })
  }, [erp, name])
  useEffect(() => {
    const controller = new AbortController()
    load(controller.signal)
    return () => controller.abort()
  }, [load])

  if (failed) {
    if (failed.status === 403) return <div className="page"><NoAccess what="this project" /></div>
    if (failed.status === 404) {
      return <div className="page"><ScreenState title="This project is not here"
        detail="It may have been removed, or you may not open it."
        actions={<button className="btn" onClick={onBack}>Back to projects</button>} /></div>
    }
    return <div className="page"><Failed message={failed.message} requestId={failed.requestId} onRetry={() => load()} /></div>
  }
  if (!detail || !active) return <div className="page"><Loading rows={8} label="Opening the project" /></div>

  const p = detail.project
  const makeable = active.packs.flatMap((pack) => pack.kinds
    .filter((k) => k.may_create && !k.parent && k.project)
    .map((k) => ({ pack, kind: k })))

  async function setStatus(status: string) {
    setBusy(true)
    try {
      setDetail(await erp.updateProject(name, { status }))
      toast('ok', status === 'Open' ? 'Project reopened.' : `Project marked ${status.toLowerCase()}.`)
    } catch (e) {
      toast('error', (e as ErpError).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="page work-item">
      <header>
        <button className="btn btn-quiet btn-sm" onClick={onBack}>← Projects</button>
        <h1 style={{ marginTop: 6 }}>{p.project_name}</h1>
        <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
          <span className={`chip ${p.status === 'Open' ? 'chip-info' : p.status === 'Completed' ? 'chip-ok' : ''}`}>{p.status}</span>
          {p.customer ? <span className="chip">{p.customer}</span> : null}
          {detail.counts.late ? <span className="chip chip-danger">{detail.counts.late} late</span> : null}
          <span className="chip">{p.name}</span>
        </div>
      </header>

      {editing ? (
        <section className="card card-pad" aria-label="Change the project">
          <ProjectForm initial={p} managerRoles={active.packs.flatMap((x) => x.project?.manager_roles ?? [])}
            onDone={() => { setEditing(false); load() }} onCancel={() => setEditing(false)} />
        </section>
      ) : null}

      <div className="work-body">
        <aside className="card card-pad work-side" aria-label="Details">
          <div className="work-side-h">Details</div>
          <Fact label="Client" value={p.customer ?? '—'} />
          <Fact label="Project manager" value={p.manager
            ? <>{nameOf(p.manager)}{p.manager_card?.designation ? <div className="small muted">{p.manager_card.designation}</div> : null}</>
            : <span className="muted">Nobody yet</span>} />
          <Fact label="Dates" value={dates(p)} />
          {p.cost_center ? <Fact label="Cost centre" value={p.cost_center} /> : null}
          <Fact label="Progress" value={<Progress value={p.percent_complete} />} />
          <Fact label="Jobs" value={`${detail.counts.open_jobs} open of ${detail.counts.jobs}`} />
          {p.notes ? <Fact label="Notes" value={<span style={{ whiteSpace: 'pre-wrap' }}>{p.notes}</span>} /> : null}
          {detail.may_edit ? (
            <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
              <button className="btn btn-sm" onClick={() => setEditing(true)} disabled={editing}>Change</button>
              {p.status === 'Open' ? (
                <button className="btn btn-sm btn-quiet" disabled={busy} onClick={() => void setStatus('Completed')}>Mark completed</button>
              ) : (
                <button className="btn btn-sm btn-quiet" disabled={busy} onClick={() => void setStatus('Open')}>Reopen</button>
              )}
            </div>
          ) : null}
        </aside>

        <div className="work-main">
          <EstimateCard name={p.name} key={`${p.name}-${p.modified ?? ''}`} />
          <section className="card card-pad" aria-label="Its work" style={{ display: 'grid', gap: 10 }}>
            <div className="row row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
              <strong>Its work</strong>
              {p.status === 'Open' ? (
                <div className="row" style={{ gap: 6 }}>
                  {makeable.map(({ pack, kind }) => (
                    <button key={kind.value} className={`btn btn-sm ${kind.group ? 'btn-primary' : ''}`}
                            onClick={() => onMake(pack.id, kind.key, p.name)}>
                      New {kind.label.toLowerCase()}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            {detail.work.length ? <WorkTable rows={detail.work} onOpen={onOpen} mayPlan={detail.may_edit}
                                             onChanged={() => load()} /> : (
              <p className="muted small">Nothing on this project yet.</p>
            )}
          </section>
        </div>
      </div>
    </div>
  )
}

/** A project's work, with who does it -- and, for whoever runs the project, a way to hand it to
 *  somebody else (owner, 2026-10-09: "I dont have a way to reassign the assignment within project"). */
function WorkTable({ rows, onOpen, mayPlan, onChanged }: {
  rows: WorkRow[]; onOpen: (n: string) => void; mayPlan?: boolean; onChanged?: () => void
}) {
  const { active } = usePacksActive()
  const nameOf = usePersonName()
  const [changing, setChanging] = useState<string | null>(null)
  return (
    <table className="tbl">
      <thead><tr>
        <th scope="col">Work</th><th scope="col">Assigned to</th><th scope="col">State</th><th scope="col">Deadline</th>
        {mayPlan ? <th scope="col"><span className="sr-only">Reassign</span></th> : null}
      </tr></thead>
      <tbody>
        {rows.map((r) => {
          const kind = findKind(active, r.jd_kind)?.kind
          const state = kind && !kind.flow ? (r.status ?? r.workflow_state) : r.workflow_state
          return [
            <tr key={r.name} tabIndex={0} onClick={() => onOpen(r.name)}
                onKeyDown={(e) => { if (e.key === 'Enter') onOpen(r.name) }}>
              <td><b>{r.subject}</b><div className="small muted">{kind?.label ?? r.jd_kind}</div></td>
              <td className="small">
                {r.assigned.length ? r.assigned.map(nameOf).join(', ')
                  : r.row_people?.length ? <>{r.row_people.map(nameOf).join(', ')}<div className="muted">on its {(kind?.words?.rows ?? 'rows').toLowerCase()}</div></>
                  : <span className="muted">Nobody yet</span>}
              </td>
              <td><span className={`chip chip-${stateTone(state)}`}>{stateLabel(state, kind?.words, kind?.flow)}</span></td>
              <td className="small" style={r.overdue ? { color: 'var(--danger)' } : undefined}>
                {r.exp_end_date ? formatDate(r.exp_end_date) : '—'}{r.overdue ? ' · late' : ''}
              </td>
              {mayPlan ? (
                <td>{!r.closed && kind ? (
                  <button className="btn btn-quiet btn-sm" aria-expanded={changing === r.name}
                          onClick={(e) => { e.stopPropagation(); setChanging(changing === r.name ? null : r.name) }}
                          onKeyDown={(e) => e.stopPropagation()}>Reassign</button>
                ) : null}</td>
              ) : null}
            </tr>,
            changing === r.name && kind ? (
              <tr key={`${r.name}-people`} className="people-change-row">
                <td colSpan={5}>
                  <PeopleChange row={r} kind={kind} onCancel={() => setChanging(null)}
                                onDone={() => { setChanging(null); onChanged?.() }} />
                </td>
              </tr>
            ) : null,
          ]
        })}
      </tbody>
    </table>
  )
}

/** The project against its estimate (PK-80): shown to whoever may see its costs, nothing to anyone else. */
function EstimateCard({ name }: { name: string }) {
  const erp = useErp()
  const [e, setE] = useState<ProjectEstimate | null>(null)
  useEffect(() => {
    const controller = new AbortController()
    erp.projectEstimate(name, controller.signal).then(setE).catch(() => setE(null))
    return () => controller.abort()
  }, [erp, name])
  if (!e || e.state === 'no estimate') return null
  const money = (v: number | null) => (v === null ? '—' : formatCurrency(v, 'INR'))
  const tone = e.state === 'over' ? 'danger' : e.state === 'running over' ? 'warn' : 'ok'
  return (
    <section className="card card-pad estimate-card" aria-label="Against the estimate">
      <div className="row row-between" style={{ gap: 8, flexWrap: 'wrap' }}>
        <strong>Against the estimate</strong>
        <span className={`chip chip-${tone}`}>{e.state === 'on track' ? 'On track' : e.state === 'over' ? 'Over' : 'Running over'}</span>
      </div>
      <p className="small" style={{ margin: '4px 0 8px' }}>{e.words}</p>
      <table className="tbl">
        <thead><tr><th /><th className="num">Estimate</th><th className="num">So far</th><th className="num">Left</th></tr></thead>
        <tbody>
          <tr><td>Cost</td><td className="num">{money(e.estimate.cost || null)}</td><td className="num">{money(e.actual.cost)}</td>
            <td className="num">{money(e.left.cost)}</td></tr>
          <tr><td>Billed</td><td className="num">{money(e.estimate.value || null)}</td><td className="num">{money(e.actual.billed)}</td>
            <td className="num">{money(e.left.to_bill)}</td></tr>
          <tr><td>Margin</td><td className="num">{money(e.estimate.margin)}</td><td className="num">{money(e.actual.margin)}</td><td /></tr>
        </tbody>
      </table>
      <p className="small muted" style={{ margin: '6px 0 0' }}>
        Cost so far: {money(e.actual.costs_in_books)} in the books{e.actual.time_at_cost ? ` + ${money(e.actual.time_at_cost)} of time at its cost rate` : ''}.
      </p>
    </section>
  )
}

function Fact({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="work-fact">
      <div className="k">{label}</div>
      <div className="v">{value}</div>
    </div>
  )
}

/* ── the form ────────────────────────────────────────────────────────── */

/** Make a project, or change one. A client the list does not have yet is added by name here. */
export function ProjectForm({ initial, managerRoles, onDone, onCancel }: {
  initial?: WorkProjectRow
  managerRoles: string[]
  onDone: (name: string, title: string) => void
  onCancel: () => void
}) {
  const erp = useErp()
  const toast = useToast()
  const [name, setName] = useState(initial?.project_name ?? '')
  const [customer, setCustomer] = useState(initial?.customer ?? '')
  const [manager, setManager] = useState(initial?.manager ?? '')
  const [start, setStart] = useState(initial?.expected_start_date ?? '')
  const [end, setEnd] = useState(initial?.expected_end_date ?? '')
  const [notes, setNotes] = useState(initial?.notes ?? '')
  const [costCentre, setCostCentre] = useState(initial?.cost_center ?? '')
  const [estCost, setEstCost] = useState(initial?.estimated_costing ? String(initial.estimated_costing) : '')
  const [estValue, setEstValue] = useState(initial?.estimate_value ? String(initial.estimate_value) : '')
  // What the books' cost centres are (PK-79): asked once; none or one, and there is nothing to choose.
  const [centres, setCentres] = useState<{ default: string | null; choices: { name: string; label: string }[] } | null>(null)
  useEffect(() => {
    const controller = new AbortController()
    erp.costCentres(controller.signal).then(setCentres).catch(() => setCentres(null))
    return () => controller.abort()
  }, [erp])
  const [newClient, setNewClient] = useState<string | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [problem, setProblem] = useState<string | null>(null)
  const [sending, setSending] = useState(false)

  async function addClient() {
    if (!newClient?.trim()) return
    try {
      const made = await erp.makeClient(newClient.trim())
      setCustomer(made.name)
      setNewClient(null)
      toast('ok', `${made.customer_name} added as a client.`)
    } catch (e) {
      setErrors((x) => ({ ...x, customer: (e as ErpError).message }))
    }
  }

  async function send(e: FormEvent) {
    e.preventDefault()
    setErrors({}); setProblem(null); setSending(true)
    const body: ProjectBody = {
      project_name: name.trim(), customer: customer || null, manager: manager || null,
      expected_start_date: start || null, expected_end_date: end || null, notes: notes.trim() || null,
      ...(centres && centres.choices.length > 1 ? { cost_center: costCentre || null } : {}),
      estimated_costing: estCost.trim() ? Number(estCost.replace(/[,\s₹]/g, '')) : null,
      estimate_value: estValue.trim() ? Number(estValue.replace(/[,\s₹]/g, '')) : null,
    }
    try {
      if (initial) {
        await erp.updateProject(initial.name, body)
        toast('ok', 'Project changed.')
        onDone(initial.name, body.project_name ?? initial.project_name)
      } else {
        const made = await erp.makeProject(body)
        toast('ok', `Project ${made.project_name} made.`)
        onDone(made.name, made.project_name)
      }
    } catch (error) {
      const err = error as ErpError
      setErrors(err.fields ?? {})
      setProblem(err.message)
    } finally {
      setSending(false)
    }
  }

  const err = (k: string) => errors[k]
  return (
    <form onSubmit={(e) => void send(e)} noValidate style={{ display: 'grid', gap: 12 }}
          aria-label={initial ? 'Change the project' : 'New project'}>
      <h2 style={{ margin: 0 }}>{initial ? 'Change the project' : 'New project'}</h2>
      {problem ? <div role="alert" className="small" style={{ color: 'var(--danger)' }}>{problem}</div> : null}
      <div className="field">
        <label htmlFor="pf_name">Project name<span aria-hidden="true"> *</span></label>
        <input id="pf_name" className="input" value={name} autoFocus
               aria-invalid={err('project_name') ? true : undefined}
               onChange={(e) => setName(e.target.value)} placeholder="e.g. Cedar Park Medical Office" />
        {err('project_name') ? <div className="small" style={{ color: 'var(--danger)' }}>{err('project_name')}</div> : null}
      </div>
      <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
        <div>
          <PlainField fieldname="customer" label="Client" fieldtype="Link" options="Customer"
                      value={customer} onChange={(v) => setCustomer(String(v ?? ''))} error={err('customer')} />
          {newClient === null ? (
            <button type="button" className="btn btn-quiet btn-sm" onClick={() => setNewClient('')}>
              + A client not in the list
            </button>
          ) : (
            <div className="row" style={{ gap: 6, marginTop: 4 }}>
              <input className="input" aria-label="New client's name" placeholder="Client's name"
                     value={newClient} onChange={(e) => setNewClient(e.target.value)}
                     onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void addClient() } }} />
              <button type="button" className="btn btn-sm" onClick={() => void addClient()}>Add</button>
              <button type="button" className="btn btn-quiet btn-sm" onClick={() => setNewClient(null)}>Cancel</button>
            </div>
          )}
        </div>
        <PeoplePicker id="pf_manager" label="Project manager" role={managerRoles}
                      value={manager ? [manager] : []} onChange={(v) => setManager(v[0] ?? '')}
                      error={err('manager')}
                      hint="New work on this project is approved by them, unless you choose someone else." />
        <PlainField fieldname="expected_start_date" label="Starts" fieldtype="Date"
                    value={start} onChange={(v) => setStart(String(v ?? ''))} error={err('expected_start_date')} />
        <PlainField fieldname="expected_end_date" label="Due" fieldtype="Date"
                    value={end} onChange={(v) => setEnd(String(v ?? ''))} error={err('expected_end_date')} />
        <div className="field">
          <label htmlFor="pf_est_cost">Estimated cost (₹)</label>
          <input id="pf_est_cost" className="input" inputMode="decimal" value={estCost} onChange={(e) => setEstCost(e.target.value)}
                 placeholder="What it should cost to do" aria-invalid={err('estimated_costing') ? true : undefined} />
          {err('estimated_costing') ? <div className="small" style={{ color: 'var(--danger)' }}>{err('estimated_costing')}</div> : null}
        </div>
        <div className="field">
          <label htmlFor="pf_est_value">Agreed value (₹)</label>
          <input id="pf_est_value" className="input" inputMode="decimal" value={estValue} onChange={(e) => setEstValue(e.target.value)}
                 placeholder="What the client pays for it" aria-invalid={err('estimate_value') ? true : undefined} />
          {err('estimate_value') ? <div className="small" style={{ color: 'var(--danger)' }}>{err('estimate_value')}</div> : null}
        </div>
        {centres && centres.choices.length > 1 ? (
          <div className="field">
            <label htmlFor="pf_cost_centre">Cost centre</label>
            <select id="pf_cost_centre" className="input" value={costCentre} onChange={(e) => setCostCentre(e.target.value)}>
              <option value="">The usual one{centres.default ? ` (${centres.choices.find((c) => c.name === centres.default)?.label ?? centres.default})` : ''}</option>
              {centres.choices.map((c) => <option key={c.name} value={c.name}>{c.label}</option>)}
            </select>
            <div className="small muted">Its invoices, purchases and time are booked here.</div>
            {err('cost_center') ? <div className="small" style={{ color: 'var(--danger)' }}>{err('cost_center')}</div> : null}
          </div>
        ) : null}
      </div>
      <div className="field">
        <label htmlFor="pf_notes">Notes</label>
        <textarea id="pf_notes" className="textarea" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>
      <div className="row" style={{ gap: 8 }}>
        <button type="submit" className="btn btn-primary" disabled={sending}>
          {sending ? 'Saving…' : initial ? 'Save' : 'Make the project'}
        </button>
        <button type="button" className="btn btn-quiet" onClick={onCancel} disabled={sending}>Cancel</button>
      </div>
    </form>
  )
}
